import { observer } from "mobx-react";
import { FC } from "react";
import {
  Box,
  IconButton,
  LinearProgress,
  Tooltip,
  Typography,
} from "@mui/material";

import { FaRegFileAlt } from "react-icons/fa";
import CloseIcon from "../../../icons/CloseIcon";
import { CheckIcon } from "../../../icons/CheckIcon";
import WarningAmberIcon from "../../../icons/WarningAmberIcon";

import { useAppStore } from "../../../../hooks/useAppStore";

const ContactSupportFilesDisplay: FC<{ disabled: boolean }> = observer(
  ({ disabled }) => {
    const { contactSupportUploadFiles, setContactSupportUploadFiles } =
      useAppStore();

    const onRemoveFile = (fileId: string) => {
      setContactSupportUploadFiles(
        contactSupportUploadFiles.filter((file) => file.id !== fileId),
      );
    };

    if (contactSupportUploadFiles.length === 0) return null;

    return (
      // Rendered inside the dropzone: keep clicks on file rows from opening the file picker
      <Box
        onClick={(event) => event.stopPropagation()}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: 1,
          width: "100%",
          cursor: "default",
        }}
      >
        {contactSupportUploadFiles.map((file) => {
          const isUploading = file.status === "uploading";
          const isError = file.status === "error";
          const isUploaded = file.status === "uploaded";
          const canRemove = !isUploading;

          return (
            <Box
              key={file.id}
              sx={{
                position: "relative",
                overflow: "hidden",
                border: "1px solid",
                borderColor: isError ? "error.main" : "success.main",
                borderRadius: "8px",
                px: 1.5,
                py: 1,
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "background.paper",
              }}
            >
              <FaRegFileAlt size="14px" style={{ minWidth: "12px" }} />
              <Typography
                noWrap
                title={file.file.name}
                sx={{ flex: 1, minWidth: 0 }}
              >
                {file.file.name}
              </Typography>

              {isUploaded ? (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    backgroundColor: "success.main",
                    color: "success.contrastText",
                    flexShrink: 0,
                  }}
                >
                  <CheckIcon size={12} />
                </Box>
              ) : null}

              {isError ? (
                <Tooltip arrow title="Failed to upload">
                  <Box
                    sx={{ display: "flex", color: "error.main", flexShrink: 0 }}
                  >
                    <WarningAmberIcon size={18} />
                  </Box>
                </Tooltip>
              ) : null}

              <IconButton
                size="small"
                aria-label="Remove file"
                disabled={disabled || !canRemove}
                onClick={() => onRemoveFile(file.id)}
                sx={{ color: "text.secondary", flexShrink: 0 }}
              >
                <CloseIcon size={14} />
              </IconButton>

              {isUploading ? (
                <LinearProgress
                  sx={{
                    position: "absolute",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    height: 3,
                    backgroundColor: "transparent",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "success.main",
                    },
                  }}
                />
              ) : null}
            </Box>
          );
        })}
      </Box>
    );
  },
);

export default ContactSupportFilesDisplay;
